import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import MessageThread from './MessageThread';
import MessageInput from './MessageInput';
import { messages as messagesStorage, conversations as conversationsStorage, userSession } from '../../../services/localStorageService';

const ChatWindow = ({ conversation, onScheduleInterview }) => {
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Mock messages for the conversation
  const mockMessages = [
    {
      id: 1,
      sender: 'employer',
      content: "Thank you for your interest in this opportunity. I\'d love to learn more about your experience in leading technical teams and driving innovation.",
      timestamp: new Date(Date.now() - 172800000),
      status: 'read'
    },
    {
      id: 2,
      sender: 'candidate',
      content: "I appreciate your interest. I have extensive experience managing cross-functional engineering teams and driving product development initiatives. What specific aspects would you like to discuss?",
      timestamp: new Date(Date.now() - 86400000),
      status: 'read'
    },
    {
      id: 3,
      sender: 'employer',
      content: "I\'m particularly interested in your approach to scaling engineering organizations and your experience with distributed teams. Could you share some insights on how you\'ve handled these challenges?",
      timestamp: new Date(Date.now() - 43200000),
      status: 'read'
    },
    {
      id: 4,
      sender: 'candidate',
      content: "Absolutely. In my previous roles, I've successfully scaled teams from 10 to 50+ engineers while maintaining high productivity and code quality. I focus on establishing clear communication channels, implementing robust processes, and fostering a culture of ownership.",
      timestamp: new Date(Date.now() - 21600000),
      status: 'read'
    },
    {
      id: 5,
      sender: 'employer',
      content: "That\'s impressive. Looking forward to our discussion about the technical architecture.",
      timestamp: new Date(Date.now() - 3600000),
      status: 'delivered'
    }
  ];

  useEffect(() => {
    if (conversation?.id) {
      // Load messages from LocalStorage
      const storedMessages = messagesStorage?.getByConversationId(conversation?.id);
      if (storedMessages?.length > 0) {
        setMessages(storedMessages?.map(msg => ({
          id: msg?.id,
          sender: msg?.senderId === (userSession?.get()?.id || 'demo_user') ? 'candidate' : 'employer',
          content: msg?.content,
          timestamp: new Date(msg.timestamp),
          status: msg?.isRead ? 'read' : 'delivered'
        })));
      } else {
        setMessages(mockMessages);
      }
      
      // Mark messages as read
      const userId = userSession?.get()?.id || 'demo_user';
      messagesStorage?.markAsRead(conversation?.id, userId);
      conversationsStorage?.markAsRead(conversation?.id, userId);
    }
  }, [conversation?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (messageText, attachments) => {
    if (!messageText?.trim() && attachments?.length === 0) return;

    const session = userSession?.get();
    const userId = session?.id || 'demo_user';
    
    // Create message in LocalStorage
    const newMessage = messagesStorage?.create({
      conversationId: conversation?.id,
      senderId: userId,
      content: messageText,
      attachments: attachments || []
    });
    
    // Add to local state
    const displayMessage = {
      id: newMessage?.id,
      sender: 'candidate',
      content: messageText,
      timestamp: new Date(newMessage.timestamp),
      status: 'delivered',
      attachments
    };
    
    setMessages(prev => [...prev, displayMessage]);

    // Simulate employer response
    setIsTyping(true);
    setTimeout(() => {
      const responses = [
        "Thank you for your message. I\'ll review this and get back to you shortly.",
        "That\'s great to hear. Let\'s discuss this further.",
        "I appreciate your insights. This aligns well with what we\'re looking for.",
        "Excellent point. Could you elaborate on that?"
      ];
      
      const responseText = responses?.[Math.floor(Math.random() * responses?.length)];
      const employerId = conversation?.participantIds?.find(id => id !== userId) || `employer_${conversation?.matchId}`;
      
      const responseMessage = messagesStorage?.create({
        conversationId: conversation?.id,
        senderId: employerId,
        content: responseText
      });
      
      setMessages(prev => [...prev, {
        id: responseMessage?.id,
        sender: 'employer',
        content: responseText,
        timestamp: new Date(responseMessage.timestamp),
        status: 'delivered'
      }]);
      
      setIsTyping(false);
    }, 2000);
  };

  const getStageColor = (stage) => {
    const colors = {
      'Initial Connection': 'bg-blue-100 text-blue-700',
      'In Conversation': 'bg-purple-100 text-purple-700',
      'Reveal Pending': 'bg-amber-100 text-amber-700',
      'Reveal Approved': 'bg-emerald-100 text-emerald-700',
      'Meeting Scheduled': 'bg-cyan-100 text-cyan-700'
    };
    return colors?.[stage] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] h-full flex flex-col">
      {/* Chat Header */}
      <div className="p-4 md:p-6 border-b border-[#E5E7EB]">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="relative flex-shrink-0">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0025cc] to-[#0025cc] flex items-center justify-center">
                <Icon name="Briefcase" size={24} color="#FFFFFF" />
              </div>
              {conversation?.isOnline && (
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-500 border-2 border-white rounded-full" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-base md:text-lg font-semibold text-[#111827] truncate">
                {conversation?.title}
              </h3>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs md:text-sm text-[#6B7280]">
                  {conversation?.companyInfo}
                </p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getStageColor(conversation?.matchStage)}`}>
                  {conversation?.matchStage}
                </span>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            {(conversation?.matchStage === 'Reveal Approved' || conversation?.matchStage === 'Meeting Scheduled') && (
              <Button
                variant="default"
                size="sm"
                iconName="Calendar"
                onClick={onScheduleInterview}
                className="hidden sm:flex"
              >
                Schedule Interview
              </Button>
            )}
            <button
              className="p-2 hover:bg-[#F6F7FB] rounded-lg transition-colors"
              aria-label="More options"
            >
              <Icon name="MoreVertical" size={20} color="#6B7280" />
            </button>
          </div>
        </div>

        {/* Encryption Notice */}
        <div className="mt-4 flex items-center gap-2 px-3 py-2 bg-emerald-50 rounded-lg">
          <Icon name="Lock" size={16} color="#10B981" />
          <p className="text-xs text-emerald-800">
            End-to-end encrypted • Messages are secure and private
          </p>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 md:p-6">
        <MessageThread messages={messages} isTyping={isTyping} />
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="border-t border-[#E5E7EB]">
        <MessageInput onSend={handleSendMessage} />
      </div>
    </div>
  );
};

export default ChatWindow;